import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import FloatingBar from './FloatingBar'
import RadioHistory from './RadioHistory'
import {
  POLL_INTERVAL,
  type PlayerConfig,
  type Station,
  type Track,
  coverUrlFor,
  fetchNowPlaying,
  fetchStation
} from './samApi'

type PlayState = 'idle' | 'loading' | 'playing'

// iOS (and iPadOS) lock volume to the hardware buttons; setting audio.volume
// has no effect, so the slider is useless there. A feature test is unreliable
// because iOS still stores the assigned value, so — like video.js / howler.js —
// we detect the platform. The mute toggle still works, so it stays.
const VOLUME_SUPPORTED = ((): boolean => {
  if (typeof navigator === 'undefined') return true
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as macOS but exposes touch points.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  return !isIOS
})()

function useStation(
  stationId: string,
  token: string,
  enabled: boolean
): Station | null {
  const [station, setStation] = useState<Station | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    fetchStation(stationId, token)
      .then((data) => {
        if (!cancelled) setStation(data)
      })
      .catch(() => {
        /* keep the static fallback */
      })
    return () => {
      cancelled = true
    }
  }, [stationId, token, enabled])

  return station
}

function useNowPlaying(
  stationId: string,
  token: string,
  enabled: boolean
): Track | null {
  const [track, setTrack] = useState<Track | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    const update = (): void => {
      fetchNowPlaying(stationId, token)
        .then((data) => {
          if (!cancelled && data) setTrack(data)
        })
        .catch(() => {
          /* leave the previous track info in place */
        })
    }
    update()
    const intervalId = window.setInterval(update, POLL_INTERVAL)
    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [stationId, token, enabled])

  return track
}

type Props = {
  config: PlayerConfig
  /** Static editor preview: no API calls, no playback. */
  preview?: boolean
  /** Start playback as soon as the stream is ready (consent was just given). */
  autoPlay?: boolean
  /** Shown in the title row while no track info is available (editor preview). */
  placeholderTitle?: string
}

function RadioPlayer({
  config,
  preview = false,
  autoPlay = false,
  placeholderTitle
}: Props) {
  const { labels } = config
  const station = useStation(config.stationId, config.token, !preview)
  const track = useNowPlaying(config.stationId, config.token, !preview)

  // A configured override wins over the relay URL the SAM API resolves.
  const streamUrl = config.streamUrl || station?.streamUrl || null

  const audioRef = useRef<HTMLAudioElement>(null)
  // Set when play is pressed before the stream URL has resolved.
  const wantsToPlayRef = useRef(false)
  const [playState, setPlayState] = useState<PlayState>('idle')

  const [volume, setVolume] = useState(80)
  const [muted, setMuted] = useState(false)

  const [coverSrc, setCoverSrc] = useState('')
  const [coverLoaded, setCoverLoaded] = useState(false)
  const [floatingDismissed, setFloatingDismissed] = useState(false)

  const startStream = (url: string): void => {
    const audio = audioRef.current
    if (!audio) return
    // Re-assign the src on every start so we always join the live edge rather
    // than resuming a stale buffer.
    audio.src = url
    setPlayState('loading')
    void audio.play().catch(() => setPlayState('idle'))
  }

  const stopStream = (): void => {
    const audio = audioRef.current
    wantsToPlayRef.current = false
    if (audio) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
    setPlayState('idle')
  }

  const togglePlayback = (): void => {
    if (preview) return
    if (playState !== 'idle') {
      stopStream()
      return
    }
    setFloatingDismissed(false)
    if (streamUrl) {
      startStream(streamUrl)
    } else {
      // Stream URL not resolved yet — start as soon as the station data lands.
      wantsToPlayRef.current = true
      setPlayState('loading')
    }
  }

  // Consent was just granted via the media-defer placeholder, which implies a
  // play intent — queue playback for as soon as the stream URL is available.
  useEffect(() => {
    if (autoPlay && !preview) {
      wantsToPlayRef.current = true
      setPlayState('loading')
    }
  }, [autoPlay, preview])

  useEffect(() => {
    const audio = audioRef.current
    if (!streamUrl || !wantsToPlayRef.current || !audio) return
    wantsToPlayRef.current = false
    audio.src = streamUrl
    void audio.play().catch(() => setPlayState('idle'))
  }, [streamUrl])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = volume / 100
    audio.muted = muted
  }, [volume, muted])

  useEffect(() => {
    if (!station) return
    setCoverSrc(
      track?.mediaItemId
        ? coverUrlFor(station, track.mediaItemId)
        : station.fallbackCover
    )
  }, [station, track])

  const handleCoverError = (): void => {
    setCoverLoaded(false)
    if (station?.fallbackCover && coverSrc !== station.fallbackCover) {
      setCoverSrc(station.fallbackCover)
    }
  }

  const silent = muted || volume === 0
  const playerClasses = [
    'radio-player',
    playState === 'loading' && 'is-loading',
    playState === 'playing' && 'is-playing',
    silent && 'is-muted',
    coverLoaded && 'has-cover'
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <div className={playerClasses}>
        <button
          type="button"
          className="radio-player__toggle"
          aria-label={playState === 'playing' ? labels.pause : labels.play}
          onClick={togglePlayback}
        >
          <span className="radio-player__spinner" aria-hidden="true" />
        </button>

        <div className="radio-player__cover" aria-hidden="true">
          {coverSrc && (
            <img
              className="radio-player__cover-img"
              alt=""
              src={coverSrc}
              onLoad={() => setCoverLoaded(true)}
              onError={handleCoverError}
            />
          )}
          <span className="radio-player__cover-fallback" aria-hidden="true" />
        </div>

        <div className="radio-player__meta">
          <p className="radio-player__station">
            <span className="radio-player__live">
              <span className="radio-player__live-dot" />
              {labels.live}
            </span>
            <span className="radio-player__station-name">
              {!config.floating && config.websiteUrl ? (
                <a
                  className="radio-player__station-link"
                  href={config.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={labels.visitWebsite}
                >
                  {station?.name ?? labels.stationName}
                </a>
              ) : (
                (station?.name ?? labels.stationName)
              )}
            </span>
          </p>
          <p className="radio-player__title">
            {track?.title ?? placeholderTitle ?? ''}
          </p>
          <p className="radio-player__artist">{track?.artist ?? ''}</p>
        </div>

        <div className="radio-player__volume">
          <button
            type="button"
            className="radio-player__mute"
            aria-label={muted ? labels.unmute : labels.mute}
            onClick={() => setMuted(!muted)}
          />
          {VOLUME_SUPPORTED && (
            <input
              type="range"
              className="radio-player__volume-slider"
              min={0}
              max={100}
              value={volume}
              aria-label={labels.volume}
              onChange={(event) => {
                const next = Number(event.target.value)
                setVolume(next)
                if (next > 0 && muted) setMuted(false)
              }}
            />
          )}
        </div>

        {/* biome-ignore lint/a11y/useMediaCaption: live radio stream — no caption track available */}
        <audio
          ref={audioRef}
          className="radio-player__audio"
          preload="none"
          onPlaying={() => setPlayState('playing')}
          onWaiting={() => {
            if (!audioRef.current?.paused) setPlayState('loading')
          }}
          onPause={() => {
            if (!wantsToPlayRef.current) setPlayState('idle')
          }}
          onError={() => setPlayState('idle')}
        />
      </div>

      <RadioHistory
        stationId={config.stationId}
        token={config.token}
        station={station}
        currentMediaItemId={track?.mediaItemId ?? ''}
        label={labels.recentlyPlayed}
        disabled={preview}
      />

      {config.floating &&
        !preview &&
        !floatingDismissed &&
        createPortal(
          <FloatingBar
            labels={labels}
            track={track}
            station={station}
            coverSrc={coverSrc}
            coverLoaded={coverLoaded}
            playState={playState}
            muted={muted}
            silent={silent}
            volume={volume}
            volumeSupported={VOLUME_SUPPORTED}
            onToggle={togglePlayback}
            onToggleMute={() => setMuted(!muted)}
            onVolume={(next) => {
              setVolume(next)
              if (next > 0 && muted) setMuted(false)
            }}
            onClose={() => setFloatingDismissed(true)}
          />,
          document.body
        )}
    </>
  )
}

export default RadioPlayer
